import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom'; 
import axios from 'axios';
import Navbar from '@/components/Navbar';
import PDFThumbnail from '@/components/PDFThumbnail';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Calendar, Eye, Newspaper, Search, clock } from 'lucide-react';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Archive = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const today = new Date().toISOString().split('T')[0];
  const [selectedSearchDate, setSelectedSearchDate] = useState(today);

  useEffect(() => {
    fetchEditions();
  }, []);

  const fetchEditions = async () => {
    try {
      const response = await axios.get(`${API}/editions`);
      // Sorting editions to show the newest first
      const sortedData = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEditions(sortedData);
    } catch (error) {
      console.error('Error fetching editions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Logic for the Filter Chips
  const applyQuickFilter = (daysAgo) => {
    const target = new Date();
    target.setDate(target.getDate() - daysAgo);
    const dateString = target.toISOString().split('T')[0];
    
    setSelectedSearchDate(dateString);
    
    // Attempt to find and navigate immediately
    const found = editions.find(e => 
      new Date(e.date).toISOString().split('T')[0] === dateString
    );

    if (found) {
      navigate(`/viewer/${found.id}`);
    } else {
      alert(`ಸಂಕ್ಷಿಪ್ತ ಮಾಹಿತಿ: ${dateString} ರ ಸಂಚಿಕೆ ಲಭ್ಯವಿಲ್ಲ.`);
    }
  };

  const handleDateSearch = () => {
    const foundEdition = editions.find(e => {
      const editionDate = new Date(e.date).toISOString().split('T')[0];
      return editionDate === selectedSearchDate;
    });

    if (foundEdition) {
      navigate(`/viewer/${foundEdition.id}`);
    } else {
      alert("ಆಯ್ಕೆ ಮಾಡಿದ ದಿನಾಂಕದ ಸಂಚಿಕೆ ಲಭ್ಯವಿಲ್ಲ.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex justify-center items-center h-[calc(100vh-64px)]">
          <div className="flex flex-col items-center">
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B91C1C] mb-4"></div>
             <p className="text-xl text-[#1E293B] font-kannada">ಲೋಡ್ ಆಗುತ್ತಿದೆ...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-6">
          <div className="max-w-2xl">
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#1E293B] mb-4"
              style={{ fontFamily: 'Noto Serif Kannada, serif' }}
            >
              {t('allEditions')}
            </h1>
            <p className="text-lg text-gray-600">ಕಲ್ಲಿನಕೂಗು ಪತ್ರಿಕೆಯ ಹಳೆಯ ಸಂಚಿಕೆಗಳ ಸಂಗ್ರಹ.</p>
          </div>

          {/* DATE SEARCH BOX */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Calendar className="h-5 w-5 text-[#B91C1C]" />
              <Input 
                type="date" 
                max={today}
                value={selectedSearchDate}
                onChange={(e) => setSelectedSearchDate(e.target.value)}
                className="border-none focus-visible:ring-0 font-bold text-gray-700 bg-transparent"
              />
            </div>
            <Button 
              onClick={handleDateSearch}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white w-full sm:w-auto px-6"
            >
              <Search className="h-4 w-4 mr-2" /> {t('search')}
            </Button>
          </div>
        </div>

        {/* QUICK FILTER CHIPS */}
        <div className="flex flex-wrap items-center gap-3 mb-10">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wider mr-2">Quick Filters:</span>
          <Button 
            variant="outline" 
            onClick={() => applyQuickFilter(0)}
            className="rounded-full border-gray-300 hover:border-[#B91C1C] hover:text-[#B91C1C]"
          >
            ಇಂದು (Today)
          </Button>
          <Button 
            variant="outline" 
            onClick={() => applyQuickFilter(1)}
            className="rounded-full border-gray-300 hover:border-[#B91C1C] hover:text-[#B91C1C]"
          >
            ನಿನ್ನೆ (Yesterday)
          </Button>
          <Button 
            variant="outline" 
            onClick={() => applyQuickFilter(7)}
            className="rounded-full border-gray-300 hover:border-[#B91C1C] hover:text-[#B91C1C]"
          >
            ಹಿಂದಿನ 7 ದಿನಗಳು
          </Button>
        </div>

        {/* ARCHIVE GRID */}
        {editions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-20 text-center border">
            <Newspaper className="h-20 w-20 mx-auto mb-4 text-gray-300" />
            <p className="text-xl text-gray-600">{t('noEditions')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {editions.map((edition) => (
              <div
                key={edition.id}
                className="group relative bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col border border-gray-100"
              >
                <div className="absolute top-2 right-2 z-10 bg-[#B91C1C] text-white px-2 py-1 rounded-md text-[10px] sm:text-xs font-bold shadow-lg">
                  {new Date(edition.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                </div>

                <PDFThumbnail pdfUrl={edition.pdfUrl} title={edition.title} />

                <div className="p-3 sm:p-4 flex flex-col flex-grow">
                  <h3 
                    className="text-sm sm:text-base font-semibold text-[#1E293B] line-clamp-2 mb-3 min-h-[2.5rem]"
                    style={{ fontFamily: 'Noto Serif Kannada, serif' }}
                  >
                    {edition.title}
                  </h3>

                  <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-500 mb-4 pt-2 border-t">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(edition.date).toLocaleDateString('en-IN')}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Eye className="h-3 w-3" />
                      <span>{edition.views || 0}</span>
                    </div>
                  </div>

                  <Link to={`/viewer/${edition.id}`} className="mt-auto">
                    <Button 
                      className="w-full bg-[#B91C1C] hover:bg-[#991B1B] text-white text-xs sm:text-sm"
                    >
                      {t('viewPaper')}
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Archive;
